<script>
    /**
     * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
     * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
     *
     * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
     */
    import { __, generateElementId } from '@oat-sa-private/ui-core';
    import Image from './Image.svelte';
    import RenderingError from 'core/error/RenderingError';

    /**
     * Renders an inline <svg> element, with integrated background and optional bay space,
     * and exposes 2 slots for different content groups
     */
    let {
        /**
         * @property {?String} svgId internal string for prefixing ids
         * @property {String} itemIdentifier for getting item context, for asset resolver
         * @property {String} label element label
         */
        svgId = generateElementId('svg'),
        itemIdentifier,
        label = __('graphic interaction'),
        /**
         * Background image
         * @property {String} imgSrc path or url to background image
         * @property {Number} imgWidth
         * @property {Number} imgHeight
         */
        imgSrc,
        imgWidth = 0,
        imgHeight = 0,
        /**
         * Optional bay coords (pre-scaled):
         * @property {Object} bayAttrs coordinates of space to be left blank
         * @property {Object} [bayAttrs.related]
         * @property {?Number} [bayAttrs.related.imgExcessWidth] additional width for svg due to bay
         * @property {?Number} [bayAttrs.related.imgExcessHeight] additional height for svg due to bay
         * @property {?Number} [bayAttrs.related.imgOffsetX] coordinate of image left edge
         * @property {?Number} [bayAttrs.related.imgOffsetY] coordinate of image top edge
         */
        bayAttrs = { related: {} },

        onmount,
        onbackgroundImageLoad,
        onbackgroundImageError,
        onmousemove,
        ontouchmove,
        onclick,

        renderContent,
        renderBay
    } = $props();

    let svgElement = $state();
    let labelId = $derived(label ? generateElementId('desc') : void 0);

    let imgOffsetX = $derived((bayAttrs.related && bayAttrs.related.imgOffsetX) || 0);
    let imgOffsetY = $derived((bayAttrs.related && bayAttrs.related.imgOffsetY) || 0);
    let svgWidth = $derived(imgWidth + ((bayAttrs.related && bayAttrs.related.imgExcessWidth) || 0));
    let svgHeight = $derived(imgHeight + ((bayAttrs.related && bayAttrs.related.imgExcessHeight) || 0));

    $effect(() => {
        if (svgElement) {
            onmount?.({ svgElement });
        }
    });

    let currentLang = $derived(getCurrentLanguage(svgElement));

    /**
     * Background load handler
     */
    function handleLoad() {
        onbackgroundImageLoad?.();
    }

    /**
     * Background load error handler
     */
    function handleError() {
        const err = new RenderingError(`background image "${imgSrc}" could not be loaded`, true);
        onbackgroundImageError?.(err);
    }

    /**
     * Gets current item language (if parent section with lang attribute is presented)
     * If parent section doesn't have lang attribute - uses application locale instead
     * @param {HtmlElement|undefined} svgRoot - root svg element
     * @returns {String|void}
     */
    function getCurrentLanguage(svgRoot) {
        if (!svgRoot) {
            return;
        }
        const parentSection = svgRoot.closest('section[lang]');
        if (!parentSection) {
            return __.getLocale();
        }
        return parentSection.getAttribute('lang');
    }

    const touchmoveNonPassive = node => {
        let listener;
        $effect(() => {
            if (listener) {
                node.removeEventListener('touchmove', listener);
                listener = null;
            }
            if (ontouchmove) {
                listener = ontouchmove;
                node.addEventListener('touchmove', listener, { passive: false });
            }
        });
        return () => {
            if (listener) {
                node.removeEventListener('touchmove', listener);
            }
        };
    };
</script>

<style>
    svg {
        display: block;
    }
</style>

{#if svgWidth && svgHeight}
    <!-- svelte-ignore a11y_no_static_element_interactions  -->
    <!-- svelte-ignore a11y_click_events_have_key_events -->
    <svg
        xmlns="http://www.w3.org/2000/svg"
        version="1.1"
        xmlns:xlink="http://www.w3.org/1999/xlink"
        xmlns:svgjs="http://svgjs.com/svgjs"
        lang={currentLang}
        width={svgWidth}
        height={svgHeight}
        viewBox="0 0 {svgWidth} {svgHeight}"
        aria-labelledby={labelId}
        style={`--shadow-filter-url: url(#${svgId}-filter-shadow)`}
        bind:this={svgElement}
        {onmousemove}
        {onclick}
        {@attach touchmoveNonPassive}>
        {#if label}
            <desc id={labelId}>{label}</desc>
        {/if}
        <defs>
            <filter id="{svgId}-filter-shadow" filterUnits="userSpaceOnUse">
                <feDropShadow dx="0" dy="0" stdDeviation="1.5" flood-color="black" />
            </filter>
        </defs>
        {@render renderBay?.()}
        <g class="image" transform="translate({imgOffsetX}, {imgOffsetY})">
            <Image
                {itemIdentifier}
                src={imgSrc}
                width={imgWidth}
                height={imgHeight}
                onload={handleLoad}
                onerror={handleError} />
            {@render renderContent?.()}
        </g>
    </svg>
{/if}
